import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import { formatPhoneNumber } from "../../../../functions";

function timeOfDay(date) {
  const d = new Date(date);
  let h = d.getHours();
  const m = d.getMinutes();
  const ampm = h >= 12 ? "pm" : "am";
  h = h % 12 || 12;
  return `${h}:${String(m).padStart(2, "0")}${ampm}`;
}

function elapsedMinutes(date, now = Date.now()) {
  const ms = now - new Date(date).getTime();
  return Math.max(0, Math.floor(ms / 60000));
}

export default function WaitlistItem({ entry, onSendText, onSeat, onRemove, onRevert, onContextMenu }) {
  const [isOpen, setOpen] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const isLocal = typeof window !== "undefined" && window.location.hostname === "localhost";

  const isWaiting       = entry.state === "waiting";
  const isTextSent      = entry.state === "text-sent";
  const isTextConfirmed = entry.state === "text-confirmed";
  const isSeated        = entry.state === "seated";
  const isCancelled     = entry.state === "cancelled";
  const canActiveAct    = isWaiting || isTextSent || isTextConfirmed;
  const isDimmed        = isSeated || isCancelled;
  const canExpand       = true;

  const events = [{ label: "Added", time: entry.dateMade }];
  if (entry.confirmTextSentAt) events.push({ label: "Text sent", time: entry.confirmTextSentAt });
  if (entry.confirmedAt) events.push({ label: "Confirmed", time: entry.confirmedAt });
  if (entry.cancelledAt) {
    const by = entry.cancelledBy || "";
    events.push({
      label: by === "Customer" ? "Customer cancelled" : by ? `Cancelled by ${by}` : "Cancelled",
      time: entry.cancelledAt,
    });
  }
  events.sort((a, b) => new Date(a.time) - new Date(b.time));

  useEffect(() => {
    if (!isWaiting) return;
    const msToNextMinute = 60000 - ((Date.now() - new Date(entry.dateMade).getTime()) % 60000);
    const timeout = setTimeout(() => setNow(Date.now()), msToNextMinute);
    return () => clearTimeout(timeout);
  }, [isWaiting, entry.dateMade, now]);

  const elapsed = elapsedMinutes(entry.dateMade, now);
  const showMeter = isWaiting;
  let meterClass = "waitlist-meter";
  if (showMeter) {
    if (elapsed >= entry.waitTime) meterClass += " waitlist-meter-over";
    else if (elapsed >= entry.waitTime * 0.75) meterClass += " waitlist-meter-warn";
  }

  return (
    <motion.div
      className={`waitlist-item${isDimmed ? " waitlist-item-dimmed" : ""}`}
      layout
      onClick={() => canExpand && setOpen(!isOpen)}
      onContextMenu={(e) => {
        if (!isLocal || !onContextMenu) return;
        e.preventDefault();
        onContextMenu(e, entry);
      }}
      role={canExpand ? "button" : undefined}
      tabIndex={canExpand ? 0 : undefined}
    >
      <div className="waitlist-item-row">
        <div className="waitlist-amount">{entry.numGuests}</div>
        <div className="waitlist-item-text">
          <div className="waitlist-item-name">{entry.name}</div>
          {entry.phone && !isDimmed && (
            <div className="waitlist-item-phone">{formatPhoneNumber(entry.phone)}</div>
          )}
        </div>
        <div className="waitlist-item-badges">
          {isTextSent && <span className="waitlist-badge waitlist-badge-sent">Text Sent</span>}
          {isTextConfirmed && <span className="waitlist-badge waitlist-badge-confirmed">Confirmed</span>}
          {isSeated && <span className="waitlist-badge waitlist-badge-seated">Seated</span>}
          {isCancelled && <span className="waitlist-badge waitlist-badge-cancelled">Cancelled</span>}
        </div>
        {showMeter && (
          <div className={meterClass}>
            <div className="waitlist-meter-elapsed">{elapsed}m</div>
            <div className="waitlist-meter-told">of ~{entry.waitTime}m</div>
          </div>
        )}
      </div>

      <AnimatePresence initial={false}>
        {isOpen && canExpand && (
          <motion.div
            key="actions"
            className="waitlist-actions"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            style={{ overflow: "hidden" }}
          >
            {isDimmed && entry.phone && (
              <div className="waitlist-item-phone waitlist-item-phone-expanded">
                {formatPhoneNumber(entry.phone)}
              </div>
            )}
            <div className="waitlist-timeline">
              {events.map((ev, i) => (
                <span key={i} className="waitlist-timeline-event">
                  {i > 0 && <span className="waitlist-timeline-dot">·</span>}
                  {ev.label} {timeOfDay(ev.time)}
                </span>
              ))}
            </div>
            <div className="waitlist-actions-inner">
              {canActiveAct && (
                <>
                  {isWaiting && entry.phone && (
                    <button
                      type="button"
                      className="waitlist-btn waitlist-btn-primary"
                      onClick={(e) => {
                        e.stopPropagation();
                        onSendText(entry);
                      }}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                        <path d="M21 11.5C21 16.1944 16.9706 20 12 20C10.5751 20 9.22773 19.6841 8.03261 19.1213L3 20L4.39499 16.0276C3.51668 14.7079 3 13.1659 3 11.5C3 6.80558 7.02944 3 12 3C16.9706 3 21 6.80558 21 11.5Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
                      </svg>
                      Text Table Ready
                    </button>
                  )}
                  <button
                    type="button"
                    className="waitlist-btn waitlist-btn-seat"
                    onClick={(e) => {
                      e.stopPropagation();
                      onSeat(entry);
                    }}
                  >
                    Seat as Walk-in
                  </button>
                  <button
                    type="button"
                    className="waitlist-btn waitlist-btn-remove"
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemove(entry);
                    }}
                    aria-label="Remove from waitlist"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                </>
              )}
              {isCancelled && (
                <button
                  type="button"
                  className="waitlist-btn waitlist-btn-tertiary"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRevert(entry);
                  }}
                >
                  Revert to Waiting
                </button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
